import React from 'react'

declare global { interface Window { TradingView?: any } }

function ensureScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.TradingView) { resolve(); return }
    const id = 'tradingview-widget-script'
    const exist = document.getElementById(id) as HTMLScriptElement | null
    if (exist && (window as any).TradingView) { resolve(); return }
    if (exist) { exist.addEventListener('load', () => resolve()); return }
    const s = document.createElement('script')
    s.id = id
    s.src = 'https://s3.tradingview.com/tv.js'
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Failed to load TradingView script'))
    document.head.appendChild(s)
  })
}

type Props = {
  symbol: string
  exchange?: string
  interval?: string
  studies?: boolean
  className?: string
  preferPerp?: boolean
  withRsi?: boolean
  timezone?: string
  locale?: string
  onVisibleRangeChange?: (range: { from: number; to: number } | null) => void
}

export default function TVChartFixed({
  symbol,
  exchange = 'BYBIT',
  interval = '60',
  studies = true,
  className,
  preferPerp = true,
  withRsi = true,
  timezone = 'Etc/UTC',
  locale = 'br',
  onVisibleRangeChange,
}: Props) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const widgetRef = React.useRef<any>(null)
  const uid = React.useMemo(() => `tv_${Math.random().toString(36).slice(2)}`, [])

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await ensureScript()
        if (cancelled) return
        const tv = (window as any).TradingView
        if (!tv || !containerRef.current) return

        if (widgetRef.current && widgetRef.current.remove) { try { widgetRef.current.remove() } catch {}; widgetRef.current = null }

        const sym = formatSymbol(exchange, symbol, preferPerp)
        widgetRef.current = new tv.widget({
          autosize: true,
          symbol: sym,
          interval,
          container_id: uid,
          timezone,
          theme: 'dark',
          style: '1',
          locale,
          toolbar_bg: 'rgba(0,0,0,0.2)',
          hide_side_toolbar: false,
          allow_symbol_change: false,
          details: false,
          withdateranges: true,
          // Cria 3 EMAs inicialmente (ajustaremos inputs/cores em seguida). Volume NÃO é forçado aqui.
          // Apenas RSI é adicionado quando solicitado.
          studies: studies ? [
            'Moving Average Exponential@tv-basicstudies',
            'Moving Average Exponential@tv-basicstudies',
            'Moving Average Exponential@tv-basicstudies',
            ...(withRsi ? ['RSI@tv-basicstudies'] : []),
          ] : [],
        })

        widgetRef.current.onChartReady(() => {
          if (!studies) return
          const chart = widgetRef.current.activeChart?.() || widgetRef.current.chart()

          function applyStudiesSafe(retries = 4) {
            try {
              const list = chart.getAllStudies?.() || []
              const lname = (x:any) => String(x?.name || '').toLowerCase()
              const isEma = (s:any) => {
                const n = lname(s)
                return n.includes('moving average exponential') || n.includes('média móvel exponencial') || n.includes('media movel exponencial') || n === 'ema'
              }
              let emas = list.filter((s:any) => isEma(s))
              // Helpers de criação/estilo
              const ensureOne = (len:number, color:string) => {
                try {
                  // Procura uma EMA existente para reaproveitar
                  let target = (chart.getAllStudies?.() || []).find((s:any) => isEma(s))
                  const create = () => {
                    let id: any = null
                    // Tenta com nomes mais comuns no widget (sem sufixo @tv-basicstudies)
                    try { id = chart.createStudy?.('Moving Average Exponential', true, false, [len]) } catch {}
                    if (!id) { try { id = chart.createStudy?.('EMA', true, false, [len]) } catch {} }
                    // Fallbacks localizados
                    if (!id) { try { id = chart.createStudy?.('Média Móvel Exponencial', true, false, [len]) } catch {} }
                    // Fallback com sufixo (alguns ambientes aceitam)
                    if (!id) { try { id = chart.createStudy?.('Moving Average Exponential@tv-basicstudies', true, false, [len]) } catch {} }
                    if (!id) { try { id = chart.createStudy?.('Média Móvel Exponencial@tv-basicstudies', true, false, [len]) } catch {} }
                    return id
                  }
                  if (!target) {
                    const id = create()
                    if (id) {
                      try { chart.setStudyInputs?.(id, { length: len }) } catch {}
                      try { chart.setStudyStyle?.(id, { 'Plot.color': color, 'Plot.linewidth': 2 }) } catch {}
                    }
                  } else {
                    try { chart.setStudyInputs?.(target.id, { length: len }) } catch {}
                    try { chart.setStudyStyle?.(target.id, { 'Plot.color': color, 'Plot.linewidth': 2 }) } catch {}
                  }
                } catch {}
              }
              // Remove EMAs antigas (evita conflitos e invisibilidade)
              try { (chart.getAllStudies?.() || []).filter((s:any)=> isEma(s)).forEach((s:any)=> { try { chart.removeStudy?.(s.id) } catch {} }) } catch {}
              const lengths = [9]
              const colors = ['#FFD600']
              // Cria novamente UMA EMA (teste)
              ensureOne(lengths[0], colors[0])
              // RSI: apenas garante presença (pane inferior) — usa estilos padrão do widget
              if (withRsi) {
                const rsi = list.find((s: any) => String(s?.name || '').toLowerCase().includes('relative strength index'))
                if (!rsi) {
                  try { chart.createStudy?.('Relative Strength Index@tv-basicstudies', false, false) } catch {}
                }
              }
            } catch {
              if (retries > 0) setTimeout(() => applyStudiesSafe(retries - 1), 500)
            }
          }

          function shrinkRsiPane(attempts = 6) {
            try {
              if (!withRsi) return
              const list = chart.getAllStudies?.() || []
              const lname = (x:any) => String(x?.name || '').toLowerCase()
              const rsiList = list.filter((s:any) => {
                const n = lname(s)
                return n.includes('relative strength index') || n.includes('indice de forca relativa') || n.includes('ifr') || n === 'rsi'
              })
              for (const s of rsiList) {
                try { chart.setStudyStyle?.(s.id, { paneSize: 'tiny' }) } catch {}
                try { chart.setStudyStyle?.(s.id, { 'Pane size': 'tiny' }) } catch {}
                try { chart.setStudyStyle?.(s.id, { 'pane size': 'tiny' }) } catch {}
                try { chart.setStudyStyle?.(s.id, { pane_size: 'tiny' }) } catch {}
                try { chart.setStudyStyle?.(s.id, { paneSize: 'small' }) } catch {}
                try { chart.setStudyStyle?.(s.id, { 'pane.height': 96 }) } catch {}
                try { chart.setStudyStyle?.(s.id, { paneHeight: 96 }) } catch {}
                try {
                  const panes = (chart as any).getPanes?.() || (chart as any).getAllPanes?.() || []
                  if (Array.isArray(panes) && panes.length > 1) {
                    const last = panes[panes.length - 1]
                    const pid = (last && (last.id || last.pane || last.paneId))
                    try { (chart as any).setPaneHeight?.(pid || last, 96) } catch {}
                    try { last?.setHeight?.(96) } catch {}
                  }
                } catch {}
              }
            } catch {
              if (attempts > 0) setTimeout(() => shrinkRsiPane(attempts - 1), 350)
            }
          }

          // Emitir range visível para sincronização externa (ex.: RSI pane)
          const emitRange = (r?: any) => {
            try {
              if (!onVisibleRangeChange) return
              const rng = r || chart.getVisibleRange?.()
              if (rng && typeof rng.from !== 'undefined' && typeof rng.to !== 'undefined') {
                onVisibleRangeChange({ from: Number(rng.from), to: Number(rng.to) })
              } else {
                onVisibleRangeChange(null)
              }
            } catch { onVisibleRangeChange && onVisibleRangeChange(null) }
          }
          try { emitRange() } catch {}

          function ensureRsiPane(retries = 6) {
            try {
              if (!withRsi) return
              const list = chart.getAllStudies?.() || []
              const lname = (x:any) => String(x?.name || '').toLowerCase()
              const hasRsi = list.some((s:any) => {
                const n = lname(s)
                return n.includes('relative strength index') || n.includes('índice de força relativa') || n.includes('indice de forca relativa') || n.includes('ifr') || n === 'rsi'
              })
              if (!hasRsi) {
                let created = false
                // Tenta criar já com overrides de tamanho do pane
                try { chart.createStudy?.('RSI@tv-basicstudies', false, false, undefined as any, { paneSize: 'small' }); created = true } catch {}
                if (!created) { try { chart.createStudy?.('RSI', false, false, undefined as any, { paneSize: 'small' }); created = true } catch {} }
                if (!created) { try { chart.createStudy?.('Relative Strength Index@tv-basicstudies', false, false, undefined as any, { paneSize: 'small' }); created = true } catch {} }
                if (!created) { try { chart.createStudy?.('Relative Strength Index', false, false, undefined as any, { paneSize: 'small' }); created = true } catch {} }
              }
              setTimeout(() => shrinkRsiPane(3), 120)
            } catch {
              if (retries > 0) setTimeout(() => ensureRsiPane(retries - 1), 400)
            }
          }

          setTimeout(() => { applyStudiesSafe(4); ensureRsiPane(6) }, 600)
          setTimeout(() => { shrinkRsiPane(5) }, 1200)
          // Chamada extra tardia para casos em que o widget atrasa a injeção dos estudos
          setTimeout(() => { applyStudiesSafe(1); ensureRsiPane(6) }, 1600)
          let pollId: any = null
          try {
            chart.onSymbolChanged?.(() => setTimeout(() => { applyStudiesSafe(1); ensureRsiPane(3); shrinkRsiPane(3); emitRange() }, 200))
            chart.onIntervalChanged?.(() => setTimeout(() => { applyStudiesSafe(1); ensureRsiPane(3); shrinkRsiPane(3); emitRange() }, 200))
            chart.onVisibleRangeChanged?.((range: any) => emitRange(range))
            // Fallback para builds que expõem onVisibleTimeRangeChanged (Charting Library-like)
            ;(chart as any).onVisibleTimeRangeChanged?.((range: any) => emitRange(range))
          } catch {}
          // Fallback: polling leve caso o evento não dispare no embed
          try {
            let lastFrom: number | null = null, lastTo: number | null = null
            pollId = setInterval(() => {
              try {
                const r = chart.getVisibleRange?.()
                const f = Number(r?.from), t = Number(r?.to)
                if (Number.isFinite(f) && Number.isFinite(t) && (f !== lastFrom || t !== lastTo)) {
                  lastFrom = f; lastTo = t; emitRange(r)
                }
              } catch {}
            }, 400)
          } catch {}

          // cleanup do polling na troca/desmonte do widget
          try { widgetRef.current?.onRemove?.(() => { if (pollId) clearInterval(pollId) }) } catch {}
        })
      } catch {}
    })()
    return () => { cancelled = true }
  }, [symbol, exchange, interval, studies, preferPerp])

  return <div ref={containerRef} id={uid} className={className || 'h-full w-full rounded-lg border border-border'} />
}

function formatSymbol(exchange: string, raw: string, preferPerp?: boolean): string {
  const s = String(raw || '').trim().toUpperCase()
  const p = s.replace(/[^A-Z0-9\.]/g, '')
  if ((preferPerp ?? true) && exchange.toUpperCase() === 'BYBIT') {
    const base = p.endsWith('.P') ? p : (p + '.P')
    return `${exchange}:${base}`
  }
  return `${exchange}:${p}`
}
